import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { tasksService, workflowStagesService } from '../../services';
import './TaskTemplatesManager.css';

const TaskTemplatesManager = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [workflowStages, setWorkflowStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedStage, setSelectedStage] = useState('');

  const [templateForm, setTemplateForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    estimated_hours: '',
    category: 'general',
    instructions: '',
    checklist_items: '',
    due_days_offset: '',
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
    fetchWorkflowStages();
  }, [selectedStage]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      // Note: We'll need to add this endpoint to the tasks API
      const data = await tasksService.getTaskTemplates(selectedStage);
      setTemplates(data);
    } catch (error) {
      console.error('Failed to fetch task templates:', error);
      setError('Failed to load task templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflowStages = async () => {
    try {
      const stages = await workflowStagesService.getAll();
      setWorkflowStages(stages);
    } catch (error) {
      console.error('Failed to fetch workflow stages:', error);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      const templateData = {
        ...templateForm,
        estimated_hours: templateForm.estimated_hours ? parseFloat(templateForm.estimated_hours) : null,
        due_days_offset: templateForm.due_days_offset ? parseInt(templateForm.due_days_offset) : null,
        checklist_items: templateForm.checklist_items ? templateForm.checklist_items.split('\n').filter(item => item.trim()) : null
      };

      await tasksService.createTaskTemplate(templateData);
      setSuccess('Task template created successfully');
      await fetchTemplates();
      setShowCreateModal(false);
      resetForm();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to create task template:', error);
      setError('Failed to create task template. Please try again.');
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    try {
      const templateData = {
        ...templateForm,
        estimated_hours: templateForm.estimated_hours ? parseFloat(templateForm.estimated_hours) : null,
        due_days_offset: templateForm.due_days_offset ? parseInt(templateForm.due_days_offset) : null,
        checklist_items: templateForm.checklist_items ? templateForm.checklist_items.split('\n').filter(item => item.trim()) : null
      };

      await tasksService.updateTaskTemplate(editingTemplate.id, templateData);
      setSuccess('Task template updated successfully');
      await fetchTemplates();
      setEditingTemplate(null);
      resetForm();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to update task template:', error);
      setError('Failed to update task template. Please try again.');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this task template? This action cannot be undone.')) {
      return;
    }

    try {
      await tasksService.deleteTaskTemplate(templateId);
      setSuccess('Task template deleted successfully');
      await fetchTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to delete task template:', error);
      setError('Failed to delete task template. Please try again.');
    }
  };

  const handleToggleActive = async (templateId, isActive) => {
    try {
      await tasksService.updateTaskTemplate(templateId, { is_active: !isActive });
      setSuccess(`Task template ${!isActive ? 'activated' : 'deactivated'} successfully`);
      await fetchTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to toggle template status:', error);
      setError('Failed to update template status. Please try again.');
    }
  };

  const handleAssignToStage = async (templateId, stageId) => {
    try {
      await tasksService.assignTemplateToStage(templateId, stageId);
      setSuccess('Template assigned to workflow stage successfully');
      await fetchTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to assign template to stage:', error);
      setError('Failed to assign template to stage. Please try again.');
    }
  };

  const handleUnassignFromStage = async (templateId, stageId) => {
    try {
      await tasksService.unassignTemplateFromStage(templateId, stageId);
      setSuccess('Template unassigned from workflow stage successfully');
      await fetchTemplates();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to unassign template from stage:', error);
      setError('Failed to unassign template from stage. Please try again.');
    }
  };

  const resetForm = () => {
    setTemplateForm({
      title: '',
      description: '',
      priority: 'medium',
      estimated_hours: '',
      category: 'general',
      instructions: '',
      checklist_items: '',
      due_days_offset: '',
      is_active: true
    });
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      title: template.title || '',
      description: template.description || '',
      priority: template.priority || 'medium',
      estimated_hours: template.estimated_hours || '',
      category: template.category || 'general',
      instructions: template.instructions || '',
      checklist_items: template.checklist_items ? template.checklist_items.join('\n') : '',
      due_days_offset: template.due_days_offset || '',
      is_active: template.is_active !== false
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'communication': return '📞';
      case 'documentation': return '📄';
      case 'follow_up': return '⏰';
      case 'research': return '🔍';
      case 'booking': return '✈️';
      case 'payment': return '💳';
      default: return '📋';
    }
  };

  if (loading && templates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading task templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Task Templates</h2>
          <p className="mt-1 text-sm text-gray-600">
            Create and manage reusable task templates for workflow automation
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Create Template
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <select
            value={selectedStage}
            onChange={(e) => setSelectedStage(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Templates</option>
            {workflowStages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                Templates for {stage.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No task templates found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Create your first task template to automate workflow tasks.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div key={template.id} className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{getCategoryIcon(template.category)}</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {template.title}
                      </h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(template.priority)}`}>
                          {template.priority}
                        </span>
                        <span className="text-xs text-gray-500 capitalize">
                          {template.category}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleToggleActive(template.id, template.is_active)}
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        template.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {template.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                </div>

                {template.description && (
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                    {template.description}
                  </p>
                )}

                <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-4">
                    {template.estimated_hours && (
                      <span>⏱️ {template.estimated_hours}h</span>
                    )}
                    {template.due_days_offset && (
                      <span>📅 +{template.due_days_offset}d</span>
                    )}
                  </div>
                </div>

                {template.assigned_stages && template.assigned_stages.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">Assigned to stages:</p>
                    <div className="flex flex-wrap gap-1">
                      {template.assigned_stages.map((stage) => (
                        <span
                          key={stage.id}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${stage.color}20`,
                            color: stage.color
                          }}
                        >
                          {stage.name}
                          <button
                            onClick={() => handleUnassignFromStage(template.id, stage.id)}
                            className="ml-1 text-gray-400 hover:text-gray-600"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditModal(template)}
                      className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                  
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAssignToStage(template.id, e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="text-xs border border-gray-300 rounded px-2 py-1"
                    defaultValue=""
                  >
                    <option value="">Assign to stage...</option>
                    {workflowStages
                      .filter(stage => !template.assigned_stages?.some(as => as.id === stage.id))
                      .map((stage) => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTemplate) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingTemplate ? 'Edit Task Template' : 'Create Task Template'}
              </h3>
              
              <form onSubmit={editingTemplate ? handleUpdateTemplate : handleCreateTemplate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={templateForm.title}
                      onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter template title"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={templateForm.category}
                      onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="general">General</option>
                      <option value="communication">Communication</option>
                      <option value="documentation">Documentation</option>
                      <option value="follow_up">Follow Up</option>
                      <option value="research">Research</option>
                      <option value="booking">Booking</option>
                      <option value="payment">Payment</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe what this task template is for"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={templateForm.priority}
                      onChange={(e) => setTemplateForm({ ...templateForm, priority: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estimated Hours
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={templateForm.estimated_hours}
                      onChange={(e) => setTemplateForm({ ...templateForm, estimated_hours: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Due Days Offset
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={templateForm.due_days_offset}
                      onChange={(e) => setTemplateForm({ ...templateForm, due_days_offset: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Days from creation"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Instructions
                  </label>
                  <textarea
                    value={templateForm.instructions}
                    onChange={(e) => setTemplateForm({ ...templateForm, instructions: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Detailed instructions for completing this task"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Checklist Items
                  </label>
                  <textarea
                    value={templateForm.checklist_items}
                    onChange={(e) => setTemplateForm({ ...templateForm, checklist_items: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter each checklist item on a new line"
                  />
                  <p className="mt-1 text-xs text-gray-500">Enter each checklist item on a separate line</p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={templateForm.is_active}
                    onChange={(e) => setTemplateForm({ ...templateForm, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    Active template (available for workflow automation)
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingTemplate(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskTemplatesManager;